import React, { useState, useEffect } from "react";
import {
  safeScrollToTop,
  shouldSkipAutoScroll,
  blockAutoScroll,
} from "../utils/scrollGuard";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { RAZORPAY_CHECKOUT_CONFIG } from "../config/razorpay.config";
import { useScrollLock } from "../context/ScrollLockContext";
import { APP_URLS } from "../config/urls.config";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaUtensils,
  FaCalendarAlt,
  FaCheckCircle,
  FaShieldAlt,
  FaPercentage,
  FaStar,
} from "react-icons/fa";
import jwtTokenService from "../services/jwtTokenService";
import { API_CONFIG } from "../config/api.config";
import { restaurantBookingAPI } from "../api/bookingApi";

const RestaurantBookingPage = () => {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = React.useRef(null);
  const { lockScroll } = useScrollLock();

  // Get data passed from navigation
  const passedRestaurant = location.state?.restaurant;
  const passedTable = location.state?.table;

  const [restaurant, setRestaurant] = useState(passedRestaurant || null);
  const [table, setTable] = useState(passedTable || null);
  const [loading, setLoading] = useState(!passedRestaurant || !passedTable);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    bookingDate: "",
    bookingTime: "",
    guests: 1,
    fullName: "",
    email: "",
    phone: "",
    specialRequests: "",
  });

  const [userData, setUserData] = useState(null);
  const [membershipTier, setMembershipTier] = useState("Basic");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    // Scroll to absolute top on mount (guarded)
    safeScrollToTop();
    try {
      if (!shouldSkipAutoScroll()) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    } catch (e) { }
  }, []);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    // Get user data
    const user = jwtTokenService.getUser();
    setUserData(user);

    // Fetch user's current membership tier from backend (matching room booking page)
    const fetchMembershipTier = async () => {
      if (user && (user.id || user.user_id || user.userId)) {
        try {
          const userId = user.id || user.user_id || user.userId;
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/profile/current-plan/${userId}`,
          );
          if (response.ok) {
            const data = await response.json();
            setMembershipTier(data.membershipTier || data.planType || "Basic");
          }
        } catch (error) {
          console.error("Error fetching membership tier:", error);
          setMembershipTier("Basic");
        }
      }
    };

    fetchMembershipTier();

    // Pre-fill form with user data
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.name || user.fullName || "",
        email: user.email || "",
        phone: user.phone || user.mobile || "",
      }));
    }

    // If data wasn't passed, fetch it (fallback)
    if (!passedRestaurant || !passedTable) {
      toast.error("Restaurant or table data not found");
      navigate("/restaurants");
    }

    return () => {
      // Cleanup: remove script on unmount
      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]',
      );
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [restaurantId, tableId]);

  // Generate time slots with 1-hour ranges (e.g., "1pm to 2pm")
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 11; hour <= 22; hour++) {
      const time = `${hour.toString().padStart(2, "0")}:00`;

      // Current hour display
      const currentHour = hour > 12 ? hour - 12 : hour;
      const currentPeriod = hour >= 12 ? "pm" : "am";

      // Next hour display
      const nextHour = hour + 1;
      const displayNextHour = nextHour > 12 ? nextHour - 12 : nextHour;
      const nextPeriod = nextHour >= 12 ? "pm" : "am";

      // Format: "1pm to 2pm"
      const displayTime = `${currentHour}${currentPeriod} to ${displayNextHour}${nextPeriod}`;

      slots.push({ value: time, label: displayTime });
    }
    return slots;
  };

  // Convert time value (e.g., "13:00") to display format (e.g., "1pm to 2pm")
  const formatTimeDisplay = (timeValue) => {
    if (!timeValue) return "Select Time";
    const hour = parseInt(timeValue.split(":")[0]);
    const currentHour = hour > 12 ? hour - 12 : hour;
    const currentPeriod = hour >= 12 ? "pm" : "am";
    const nextHour = hour + 1;
    const displayNextHour = nextHour > 12 ? nextHour - 12 : nextHour;
    const nextPeriod = nextHour >= 12 ? "pm" : "am";
    return `${currentHour}${currentPeriod} to ${displayNextHour}${nextPeriod}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userData) {
      toast.error("Please login to book a table");
      navigate("/login");
      return;
    }

    // Validate table data
    if (!table || (!table.table_id && !table.tableId && !table.id)) {
      toast.error("Table information is missing. Please select a table again.");
      navigate(`/restaurant/${restaurantId}`);
      return;
    }

    setSubmitting(true);
    try {
      // Get tableId from multiple possible property names
      const tableIdValue = table.table_id || table.tableId || table.id;

      const bookingData = {
        restaurantId: restaurant.restaurantId,
        userId: userData.id,
        tableId: tableIdValue,
        bookingDate: formData.bookingDate,
        bookingTime: formData.bookingTime,
        guests: parseInt(formData.guests),
        customerName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        specialRequests: formData.specialRequests || "",
        totalAmount: finalAmount, // Include the calculated total with membership discount
        membershipTier: membershipTier, // Send membership tier to backend
        discount: priceDetails.discount, // Send discount amount
      };

      // Step 1: Create Razorpay payment order
      const result =
        await restaurantBookingAPI.createBookingWithPayment(bookingData);

      const {
        razorpayOrder,
        razorpayKey,
        pnr,
        bookingData: serverBookingData,
      } = result;

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        toast.error(
          "Payment system not loaded. Please refresh the page and try again.",
        );
        setSubmitting(false);
        return;
      }

      // Step 2: Initialize Razorpay payment
      const options = {
        key: razorpayKey,
        amount: razorpayOrder.amount,
        currency: "INR",
        name: "Trivana Hospitality",
        description: `Table Booking - ${restaurant.name}`,
        order_id: razorpayOrder.orderId,
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
        },
        handler: async (paymentResponse) => {
          try {
            // Step 3: Verify payment and create booking in database
            const verifyUrl = new URL(
              `${APP_URLS.API}/restaurant-bookings/verify-payment-and-create`
            );
            verifyUrl.searchParams.append("verifyBaseUrl", APP_URLS.BOOKING_VERIFY_QR);

            const verifyResponse = await fetch(
              verifyUrl.toString(),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                  pnr: pnr,
                  bookingData: serverBookingData,
                }),
              },
            );

            if (!verifyResponse.ok) {
              throw new Error("Payment verification failed");
            }

            const verifyData = await verifyResponse.json();
            const createdBooking = verifyData.booking;
            const finalPnr = verifyData.pnr;

            // Show success message
            toast.success(
              `🎉 Table booking confirmed successfully!\n📋 Your PNR: ${finalPnr}\n📧 Confirmation email sent with PDF receipt!`,
              { autoClose: 5000 },
            );

            // Navigate to profile/bookings page after success
            setTimeout(() => {
              navigate("/profile", { state: { activeTab: "bookings" } });
            }, 2000);
          } catch (error) {
            console.error("❌ Payment verification failed:", error);
            toast.error(
              "Payment verification failed. Please contact support with your payment ID.",
            );
            setSubmitting(false);
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          restaurant_name: restaurant.name,
          booking_date: formData.bookingDate,
          booking_time: formData.bookingTime,
        },
        theme: {
          color: "#EE2E24",
        },
        modal: {
          ondismiss: function () {
            toast.warning("Payment cancelled. Your booking was not completed.");
            setSubmitting(false);
          },
        },
        ...RAZORPAY_CHECKOUT_CONFIG,
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Booking failed:", error);
      toast.error(
        error.response?.data?.error ||
        error.message ||
        "Booking failed. Please try again.",
      );
      setSubmitting(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Get maximum date (90 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90);
    return maxDate.toISOString().split("T")[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">
            Loading booking details...
          </p>
        </div>
      </div>
    );
  }

  if (!restaurant || !table) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600">
            Restaurant or Table not found
          </h2>
          <button
            onClick={() => navigate("/restaurants")}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Browse Restaurants
          </button>
        </div>
      </div>
    );
  }

  // Calculate pricing with membership discounts and taxes (matching room booking page)
  const calculateTotal = () => {
    const tablePrice = table?.price_per_person || table?.pricePerPerson || 100; // Price per table (not per person)
    const basePrice = tablePrice;

    // Calculate discount based on membership tier (matching backend and room booking logic)
    let discountPercentage = 0;
    switch (membershipTier.toUpperCase()) {
      case "PLATINUM":
        discountPercentage = 0.15; // 15%
        break;
      case "GOLD":
        discountPercentage = 0.1; // 10%
        break;
      case "SILVER":
        discountPercentage = 0.05; // 5%
        break;
      default:
        discountPercentage = 0; // Basic - no discount
    }

    const discount = Math.round(basePrice * discountPercentage);
    const priceAfterDiscount = basePrice - discount;
    const platformFee = parseFloat((priceAfterDiscount * 0.025).toFixed(2)); // 2.5% Platform Fee (exact decimal)
    const total = parseFloat((priceAfterDiscount + platformFee).toFixed(2)); // Keep decimal, don't round

    return {
      basePrice,
      discount,
      discountPercentage: discountPercentage * 100, // Return as percentage number
      platformFee,
      total,
    };
  };

  const priceDetails = calculateTotal();
  const advancePayment = priceDetails.total; // Full amount as advance
  const discount = priceDetails.discount;
  const finalAmount = priceDetails.total;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", fontFamily: "'Inter', sans-serif" }}>
      {/* Premium Header - More Minimalist */}
      <div
        style={{
          background: "white",
          borderBottom: "1px solid #f0f0f0",
          boxShadow: "0 2px 10px rgba(0,0,0,0.02)",
        }}
        className="sticky top-0 z-50 py-2.5 md:py-3"
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold shadow-md text-xs">T</div>
            <h1 className={`${isMobile ? 'text-sm' : 'text-xl'} font-black tracking-tight text-gray-900 uppercase`}>
              Review <span className="text-red-600">Booking</span>
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-widest">
            <span className="text-red-600">Review</span>
            <span className="w-8 h-[2px] bg-gray-200"></span>
            <span>Payment</span>
            <span className="w-8 h-[2px] bg-gray-200"></span>
            <span>Confirm</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 md:py-4">
        {/* Back Button - Sleek */}
        <button
          onClick={() => {
            lockScroll();
            try {
              if (typeof sessionStorage !== "undefined") {
                sessionStorage.setItem("focusRestaurantId", String(restaurantId));
              }
            } catch (e) { }
            navigate(`/restaurants#r-${restaurantId}`, {
              state: { restaurant, table, focusOnCard: true },
            });
          }}
          className={`${isMobile ? 'mb-3' : 'mb-5'} flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-100 text-gray-400 font-extrabold text-[9px] md:text-[10px] uppercase tracking-widest rounded-full hover:bg-gray-50 hover:text-red-600 hover:border-red-100 transition-all duration-500 shadow-sm hover:shadow-md group`}
        >
          <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
          <span>{isMobile ? "Back" : "Return To Selection"}</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          {/* Left Section - Elegant Booking Form (3 columns) */}
          <div className="lg:col-span-3 order-2 lg:order-1 flex flex-col">
            <div
              className={`rounded-2xl md:rounded-3xl overflow-hidden border border-white flex-grow flex flex-col`}
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 15px 40px rgba(0, 0, 0, 0.04)",
              }}
            >
              {/* Step Header - Sleek & Modern */}
              <div
                className={`${isMobile ? 'px-4 py-3' : 'px-8 py-4'} flex items-center justify-between border-b border-gray-50`}
                style={{ background: "linear-gradient(to right, #ffffff, #fffafa)" }}
              >
                <div className="flex items-center gap-3 md:gap-4">
                  <div className={`${isMobile ? 'w-8 h-8' : 'w-9 h-9'} bg-red-600/10 rounded-2xl flex items-center justify-center text-red-600 font-extrabold shadow-sm`}>
                    1
                  </div>
                  <div>
                    <h2 className={`${isMobile ? 'text-xs' : 'text-sm'} font-black text-gray-900 leading-none`}>Guest Information</h2>
                    <p className={`${isMobile ? 'text-[8px]' : 'text-[9px]'} text-gray-400 font-bold uppercase tracking-[0.2em] mt-1`}>Personal Details</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 bg-green-500/10 rounded-full`}>
                  <FaShieldAlt className="text-green-500 text-[8px] md:text-[10px]" />
                  <span className={`${isMobile ? 'text-[7px]' : 'text-[9px]'} font-black text-green-600 uppercase tracking-tighter`}>SECURE STAY</span>
                </div>
              </div>

              <div className={`${isMobile ? 'p-3.5' : 'p-7'} flex-grow`}>
                <form onSubmit={handleSubmit} className={`${isMobile ? 'gap-4' : 'space-y-5'} flex flex-col`}>
                  {/* Personal Details Section */}
                  <div className={`flex flex-col ${isMobile ? 'gap-2.5' : 'gap-5'}`}>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1 block">Full Name</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="Your Name"
                          className="w-full pl-9 pr-4 py-2 text-[10px] md:text-xs border border-gray-100 rounded-xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/20"
                          required
                        />
                        <FaUsers className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]" />
                      </div>
                    </div>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1 block">Email Address</label>
                      <div className="relative">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Email Address"
                          className="w-full pl-9 pr-4 py-2 text-[10px] md:text-xs border border-gray-100 rounded-xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/20"
                          required
                        />
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 font-bold text-[10px]">@</div>
                      </div>
                    </div>
                  </div>

                  <div className={`grid grid-cols-2 ${isMobile ? 'gap-2.5' : 'gap-6'}`}>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1 block">Contact</label>
                      <div className="flex gap-1.5">
                        <div className="relative w-12 md:w-24">
                          <select className="w-full appearance-none pl-2 pr-4 md:pr-8 py-2 md:py-3 text-[10px] md:text-xs border-2 border-gray-100 rounded-xl bg-gray-50/30 font-extrabold text-gray-800 focus:outline-none">
                            <option>+91</option>
                          </select>
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Phone"
                          className="flex-1 px-3 py-2 md:py-3 text-[10px] md:text-xs border-2 border-gray-100 rounded-xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/30"
                          required
                        />
                      </div>
                    </div>
                    <div className="relative group">
                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1 mb-1 block">Guests</label>
                      <div className="relative">
                        <select
                          name="guests"
                          value={formData.guests}
                          onChange={handleInputChange}
                          className="w-full pl-8 pr-6 py-2 md:py-3 text-[10px] md:text-xs border-2 border-gray-100 rounded-xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/30 appearance-none"
                          required
                        >
                          {[...Array(table.seating_capacity || 8)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} {i === 0 ? "Pers." : "Pers."}
                            </option>
                          ))}
                        </select>
                        <FaUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 transition-colors text-[10px]" />
                      </div>
                    </div>
                  </div>

                  {/* Date & Time Selection Section */}
                  <div className={`${isMobile ? 'p-3' : 'p-6'} bg-red-50/50 rounded-[1.5rem] md:rounded-[2rem] border-2 border-red-100 relative overflow-hidden group`}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full -translate-x-[-20%] -translate-y-[50%] blur-3xl"></div>
                    <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      <div className="space-y-1">
                        <label className="text-[8px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1 leading-none">{isMobile ? "Date" : "Booking Date"}</label>
                        <input
                          type="date"
                          name="bookingDate"
                          value={formData.bookingDate}
                          onChange={handleInputChange}
                          min={getMinDate()}
                          max={getMaxDate()}
                          className="w-full px-2 md:px-4 py-1.5 md:py-2 text-[9px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white shadow-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1 text-right">
                        <label className="text-[8px] md:text-[9px] font-black text-red-500/60 uppercase tracking-widest px-1 leading-none">{isMobile ? "Time" : "Booking Time"}</label>
                        <select
                          name="bookingTime"
                          value={formData.bookingTime}
                          onChange={handleInputChange}
                          className="w-full px-2 md:px-4 py-1.5 md:py-2 text-[9px] md:text-[11px] border-2 border-white rounded-xl focus:outline-none focus:border-red-500 transition-all font-black text-gray-800 bg-white shadow-sm"
                          required
                        >
                          <option value="">Time</option>
                          {getTimeSlots().map((slot) => (
                            <option key={slot.value} value={slot.value}>{slot.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Special Requests */}
                  <div className="space-y-1 group">
                    <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest px-1 leading-none">Special Requests (Opt.)</label>
                    <input
                      type="text"
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      placeholder={isMobile ? "e.g. Birthday celebration..." : "e.g. Birthday celebration, Window seat, High chair..."}
                      className="w-full px-4 py-2 text-[9px] md:text-xs border-2 border-gray-100 rounded-xl focus:outline-none focus:border-red-500 transition-all font-bold text-gray-800 bg-gray-50/30"
                    />
                  </div>

                  {/* Submit Area - High Fidelity */}
                  <div className={`${isMobile ? 'pt-3' : 'pt-4 border-t'} border-gray-100 mt-auto`}>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full ${isMobile ? 'py-3.5' : 'py-3'} px-6 rounded-xl font-black text-[11px] md:text-xs text-white transition-all duration-300 transform hover:translate-y-[-1px] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl flex items-center justify-center gap-2 group relative overflow-hidden`}
                      style={{
                        background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                        boxShadow: "0 10px 30px rgba(238, 46, 36, 0.25)",
                      }}
                    >
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                      <div className="absolute -left-10 top-0 w-20 h-full bg-white/10 skew-x-[-25deg] group-hover:left-[110%] transition-all duration-800"></div>
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                          <span className="tracking-widest uppercase">Securing...</span>
                        </>
                      ) : (
                        <>
                          <FaShieldAlt className="text-white/70 text-[10px]" />
                          <span className="tracking-widest uppercase">{isMobile ? "PAY NOW" : "Finalize & Pay Now"}</span>
                          <span className="text-sm transition-transform group-hover:translate-x-1">→</span>
                        </>
                      )}
                    </button>
                    <div className="flex items-center justify-center gap-3 mt-3 px-2 opacity-60">
                      <p className="text-[7.5px] md:text-[8px] text-gray-500 font-black uppercase tracking-widest flex items-center gap-1">
                        <FaShieldAlt className="text-green-500 text-[6px] md:text-[7px]" /> SSL Secure
                      </p>
                      <div className="h-2 w-[1px] bg-gray-300"></div>
                      <p className="text-[7.5px] md:text-[8px] text-gray-500 font-black uppercase tracking-widest flex items-center gap-1">
                        <FaCheckCircle className="text-blue-500 text-[6px] md:text-[7px]" /> Instant
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>

          </div>

          {/* Right Section - Elegant Booking Summary (2 columns) */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex flex-col">
            <div
              className="rounded-3xl overflow-hidden sticky top-24 border border-white flex-grow flex flex-col"
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.06)",
              }}
            >
              {/* Restaurant Image - More Premium */}
              <div className={`${isMobile ? 'h-32' : 'h-48'} relative group overflow-hidden`}>
                <img
                  src={restaurant?.photoUrl || restaurant?.image || restaurant?.photo_url || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&auto=format&fit=crop&q=80"}
                  alt="Restaurant"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-2 py-0.5 flex items-center gap-1 shadow-lg">
                    <FaStar className="text-yellow-400 text-[10px]" />
                    <span className="font-black text-[9px] text-white">{restaurant.rating || "4.5"}</span>
                  </div>
                  <div className="bg-red-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md shadow-lg">
                    TABLE {table.table_number || table.tableNumber || "RSV"}
                  </div>
                </div>
              </div>

              <div className={`${isMobile ? 'p-5' : 'p-6'}`}>
                {/* Restaurant & Table Details */}
                <div className={`${isMobile ? 'mb-2' : 'mb-4'}`}>
                  <h3 className={`${isMobile ? 'text-xs' : 'text-base'} font-black text-gray-900 leading-tight mb-0.5 uppercase tracking-tight`}>
                    {restaurant.name || restaurant.restaurantName || "Trivana Signature"}
                  </h3>
                  <div className={`flex items-center gap-1 text-[9px] text-gray-400 font-bold uppercase tracking-wide`}>
                    <FaMapMarkerAlt className="text-red-500" />
                    <span className="line-clamp-1">{restaurant.location || restaurant.address || "Main Street"}</span>
                  </div>
                </div>

                <div className={`${isMobile ? 'mb-4 p-3' : 'mb-5 p-4'} bg-gray-50/50 rounded-[1.5rem] md:rounded-[2rem] border border-gray-100 space-y-4`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 text-center">
                      <p className={`${isMobile ? 'text-[8px]' : 'text-[9px]'} font-black text-gray-400 uppercase tracking-widest mb-1`}>Date</p>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-black text-gray-800`}>{formData.bookingDate ? new Date(formData.bookingDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Select'}</p>
                    </div>
                    <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100`}>
                      <FaUtensils className={`text-red-500 ${isMobile ? 'text-[8px]' : 'text-[10px]'}`} />
                    </div>
                    <div className="flex-1 text-center">
                      <p className={`${isMobile ? 'text-[8px]' : 'text-[9px]'} font-black text-gray-400 uppercase tracking-widest mb-1`}>Time</p>
                      <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-black text-gray-800 line-clamp-1`}>{formData.bookingTime ? formatTimeDisplay(formData.bookingTime).split(' to ')[0] : 'Select'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-dashed border-gray-200">
                    <div className="flex items-center gap-1.5">
                      <FaUsers className={`text-red-500 ${isMobile ? 'text-[8px]' : 'text-[10px]'}`} />
                      <span className={`${isMobile ? 'text-[9px]' : 'text-[10px]'} font-bold text-gray-600`}>{table.area_type || table.areaType || 'Signature'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaUsers className={`text-red-500 ${isMobile ? 'text-[8px]' : 'text-[10px]'}`} />
                      <span className={`${isMobile ? 'text-[9px]' : 'text-[10px]'} font-bold text-gray-600`}>{formData.guests} Guest(s)</span>
                    </div>
                  </div>
                </div>

                {/* Pricing Breakdown - More Detailed */}
                <div className={`${isMobile ? 'p-3' : 'p-4'} bg-gray-50/50 rounded-2xl border border-gray-100 space-y-2`}>
                  <div className={`flex justify-between items-center ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                    <span className="text-gray-500 font-bold uppercase tracking-widest text-[8px] md:text-[10px]">Standard Reservation</span>
                    <span className="font-black text-gray-900">₹{priceDetails.basePrice}</span>
                  </div>

                  {priceDetails.discount > 0 && (
                    <div className={`flex justify-between items-center ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                      <span className="text-green-600 font-bold flex items-center gap-1 uppercase tracking-widest text-[8px] md:text-[10px]">
                        <FaPercentage className="text-[8px]" /> Discount
                      </span>
                      <span className="font-black text-green-600">-₹{priceDetails.discount}</span>
                    </div>
                  )}

                  <div className={`flex justify-between items-center ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                    <span className="text-gray-500 font-bold uppercase tracking-widest text-[8px] md:text-[10px]">Platform Fee</span>
                    <span className="font-black text-gray-900">₹{priceDetails.platformFee.toFixed(2)}</span>
                  </div>

                  <div className="pt-2 mt-2 border-t border-dashed border-gray-200 flex justify-between items-center">
                    <div>
                      <p className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Payable</p>
                      <p className={`${isMobile ? 'text-xl' : 'text-2xl'} font-black text-gray-900 leading-none mt-1`}>
                        ₹{finalAmount.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-green-500/10 px-2 py-1 rounded-md">
                      <span className="text-[7.5px] md:text-[8px] font-black text-green-600 uppercase tracking-tighter">Tax Included</span>
                    </div>
                  </div>
                </div>

                {/* Final Confirmation Note */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between gap-2 md:gap-4 text-center">
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    <span className="text-[7.5px] md:text-[9px] font-black text-gray-500 uppercase tracking-widest">Chef-Signed</span>
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span className="text-[7.5px] md:text-[9px] font-black text-gray-500 uppercase tracking-widest">Priority</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantBookingPage;
